// Documentacion.actions.ts
import { queryClient } from '@/shared/query-client/query-client';
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from 'axios';
import { UseFetchEnabledParams, UseMutationsParams, UseUpdateParams } from "@/shared/interfaces";
import { urlBackend } from "@/shared/constants";
import { erpApi } from "@/shared/axios";
import { getUrlParamsFetch } from "@/shared/utils";
import { toApiError } from "@/shared/utils/general";
import { useAuthStore } from '@/store/auth';
import { Documentacion, DocumentacionForm, DocumentacionPagingResp, DocumentacionParams } from "@/shared/interfaces/erp/documentos/Documentacion";

const { get, post, put, remove } = erpApi();

// get

export const useFetchGetDocumentacion = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<DocumentacionParams> = {}) => {
  return useQuery({
    queryKey: ['documentacion', ...Object.values(params ?? {})],
    queryFn: () => getDocumentacion(params),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const getDocumentacion = async (
  params?: DocumentacionParams
) => {
  const queryParams = getUrlParamsFetch(params ?? {});
  return get<DocumentacionPagingResp>(`${urlBackend}api/v1/documentacion/?${queryParams}`, true);
};

// create

export const useFetchPostDocumentacion = ({
  returnUrl,
  navigate
}: UseMutationsParams = {}) => {

  return useMutation({
    mutationFn: (data: DocumentacionForm) => {
      return postDocumentacion(data)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['documentacion']
      });

      navigate && returnUrl && navigate(returnUrl);

      toast.success(data.message);

    },
    onError: ((error) => {
      const data = toApiError(error)
      toast.error(data.message)
    }),
    onSettled: () => {

    },
  });
};

export const postDocumentacion = async (data: DocumentacionForm) => {
  return await post<Documentacion>(`${urlBackend}api/v1/documentacion/`, data, true);
};

// filter by id

export const useFetchGetByIdDocumentacion = (id: string) => {
  return useQuery({
    queryKey: ['documentacion', id],
    queryFn: async () => {
      return getByIdDocumentacion(id);
    },
    refetchOnWindowFocus: false,
  });
};

export const getByIdDocumentacion = async (id: string) => {
  const res = await get<Documentacion>(`${urlBackend}api/v1/documentacion/${id}`, true);
  return res;
};

// update

export const useFetchPutDocumentacion = ({
  navigate,
  returnUrl
}: UseMutationsParams = {}) => {

  return useMutation({
    mutationFn: (data: UseUpdateParams<DocumentacionForm>) => {
      return putDocumentacion(data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ['documentacion']
      });

      navigate && returnUrl && navigate(returnUrl);

      toast.success(res.message);
    },
    onError: (error) => {
      const data = toApiError(error)
      toast.error(data.message)
    },
    onSettled: () => {

    },
  });
};

export const putDocumentacion = async ({
  id,
  data
}: UseUpdateParams<DocumentacionForm>) => {
  return put<Documentacion>(`${urlBackend}api/v1/documentacion/${id}/`, data, true);
};

// delete

export const useFetchDeleteDocumentacion = () => {
  return useMutation({
    mutationFn: (id: string) => deleteDocumentacion(id),
  });
};

export const deleteDocumentacion = async (id: string) => {
  return remove<DocumentacionPagingResp>(
    `${urlBackend}api/v1/documentacion/${id}/`,
    true
  );
};


// multiple

export type SubirDocumentacionArchivosData = {
  files: File[];
  modelo: string;
  referencia_modelo: string;
};

export const postDocumentacionArchivos = async (
  data: SubirDocumentacionArchivosData
) => {
  const token = useAuthStore.getState().token;
  const formData = new FormData();

  data.files.forEach((file) => {
    formData.append('archivos', file);
  });
  formData.append('modelo', data.modelo);
  formData.append('referencia_modelo', data.referencia_modelo);

  const response = await axios.post(
    `${urlBackend}api/v1/documentacion/multiple/`,
    formData,
    {
      headers: {
        // 'Content-Type': 'multipart/form-data',
        Authorization: `Token ${token}`,
      },
    }
  );

  return response.data;
};

export const useFetchPostDocumentacionArchivos = ({
  returnUrl,
  navigate,
}: UseMutationsParams = {}) => {
  return useMutation({
    mutationFn: postDocumentacionArchivos,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['documentacion'],
      });

      navigate && returnUrl && navigate(returnUrl);

      toast.success(data.message || 'Archivos subidos correctamente');
    },
    onError: (error) => {
      const data = toApiError(error);
      toast.error(data.message);
    },
  });
};